import { useTenantNavigate, openWithTenant } from '@app-store'
import {
  ConfirmDialogWithReason,
  CustomButton,
  Loader,
  Modal,
  SvgArrowOut,
  SvgDownloadGrey,
  SvgShiftIcon,
  SvgVerticalSplitter,
  TabInterface,
  Tabs,
  Tooltip,
} from '@ui-kit'
import { useEffect, useState } from 'react'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import FaxInfo from './FaxInfo'
import {
  ATTACHMENT_FIELD_OPTIONS,
  EXTENSIONS,
  NAMESPACES,
  RECORD_STATE,
} from '../../utils/constants'
import {
  addToaster,
  discardFaxById,
  discardWebIntakeApi,
  fetchHcpFormPdfUrl,
  fetchTypeStatusForCommunications,
  hideLoader,
  ocrSplitApi,
  showLoader,
  splitFaxApi,
} from '@app-store'
import { v4 as uuid } from 'uuid'
import { FaxDetailsData } from '../../types/intakeQueue.types'
import {
  EMAIL_PREVIEW,
  FAX_PREVIEW,
  WEB_PREVIEW,
} from '../../constants/intakeQue.constants'
import { useParams } from 'react-router-dom'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import CommunicationPreview from './CommunicationPreview'
import SplitFax from './SplitFax'
import { attachments } from '../../mock-data/intakeEmailDummyData.mock'
import { EntityLayoutResponse } from '../../types/layout.types'
import { ShortcutKeys } from '../../constants/form.constants'
import DocumentPopout from '../../components/send-manual-communication/DocumentPopout'
import { fetchDownloadUrlForSingleFIles } from '../../apis/fetchPresignedUrl.api'
import { FileType } from '../../utils/types'
import { permissionObject } from '../../utils/helperFunctions'

interface FaxDetails {
  intakeType: string
  faxDetails: FaxDetailsData | null
  setPanelSize: (value: boolean | ((prevVar: boolean) => boolean)) => void
  unlockOnDiscard: React.MutableRefObject<boolean>
  allLayouts?: EntityLayoutResponse | null
  isWorkflow: boolean
  entityName?: string
  agentDetails?: {
    record_status: string
    last_name: string
    email: string
    first_name: string
    id: number
  } | null
  isAePcStatusVisible?: boolean
  isOCRReview?: boolean
}

const FaxDetails: React.FC<FaxDetails> = ({
  intakeType,
  faxDetails,
  setPanelSize,
  unlockOnDiscard,
  allLayouts,
  isWorkflow,
  agentDetails,
  isAePcStatusVisible,
  isOCRReview,
}) => {
  const dispatch = useAppDispatch()
  const { entityid, layoutid, communicationId, caseEntityId } = useParams()
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const programPermissions = useAppSelector(
    (state: RootState) => state?.clientPermissions?.programPermissions
  )
  const communicationIdFaxDetails =
    faxDetails?.communication_id?.toString() ?? ''
  const isFaxWithDecimal = communicationIdFaxDetails.includes('.')
  const parentFaxId = communicationIdFaxDetails
    ?.split('.')
    ?.slice(0, -1)
    .join('.')

  const getFaxIdLabel = (
    isFaxWithDecimal: boolean,
    communicationType: string
  ): string => {
    const isMailType = communicationType === QUEUE_STRINGS.MAIL
    if (isFaxWithDecimal) {
      return isMailType
        ? FAX_PREVIEW.ORIGINATED_FROM_MAIL_ID
        : FAX_PREVIEW.ORIGINATED_FROM_FAX_ID
    }
    return isMailType ? FAX_PREVIEW.MAIL_ID : FAX_PREVIEW.FAX_ID
  }

  const faxIdFormatted = `${getFaxIdLabel(
    isFaxWithDecimal,
    faxDetails?.category_communication_type?.label ?? ''
  )} ${isFaxWithDecimal ? parentFaxId : `- ${communicationIdFaxDetails}`}`

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [faxUrl, setFaxUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [previewData, setPreviewData] = useState({
    subject: '',
    content: '',
    id: '',
    isPreview: false,
  })
  const [isSplitFax, setIsSplitFax] = useState<boolean>(false)
  const [allTabs, setAllTabs] = useState<TabInterface[]>([])
  const [activeTab, setActiveTab] = useState<TabInterface | null>()
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
  const navigate = useTenantNavigate()

  const openWindow = () => {
    const currTabIndex = allTabs.findIndex(
      (tab) => tab.label === activeTab?.label
    )
    const url = new URL(window.location.href)
    url.search = '' // Removes all query parameters
    url.hash = '' // Removes the hash
    // TODO: might need later
    // window.history.replaceState({}, document.title, url.toString());

    // Ensure there's no trailing slash in the pathname
    const basePath = url.pathname.endsWith('/')
      ? url.pathname.slice(0, -1)
      : url.pathname
    const popupUrl = `${url.origin}${basePath}/popup/${
      currTabIndex === -1 ? 0 : currTabIndex
    }`

    openWithTenant(popupUrl, '_blank', 'width=600,height=450,left=200,top=200')
  }

  const setPanelSizeData = () => {
    setPanelSize((prev) => !prev)
  }

  const discardFax = () => {
    setIsModalOpen(true)
  }

  const handleOptionClick = (
    option: string,
    attachmentName: {
      attachment_name: string
      id?: string
      url?: string
    }
  ) => {
    if (option === ATTACHMENT_FIELD_OPTIONS.DOWNLOAD_LOWERCASE) {
      console.log(`${JSON.stringify(attachmentName)}`)
    } else if (option === ATTACHMENT_FIELD_OPTIONS.PREVIEW_LOWERCASE) {
      setPreviewData({
        isPreview: true,
        subject: attachmentName.attachment_name,
        content: `${attachmentName.attachment_name}`,
        id: attachmentName.id ?? '',
      })
    }
  }
  const onSubmit = async (reason: string) => {
    if (intakeType === QUEUE_STRINGS?.FAX) {
      try {
        dispatch(showLoader())
        await discardFaxById(
          {
            id: communicationId ?? '',
            reason: reason,
            reviewer: userId ?? 0,
          },
          faxDetails?.programs?.id?.toString() ??
            faxDetails?.hcp_program?.id?.toString()
        )
        unlockOnDiscard.current = false
        dispatch(
          addToaster({
            message:
              faxDetails?.category_communication_type?.label ===
              QUEUE_STRINGS.MAIL
                ? QUEUE_STRINGS.MAIL_DISCARDED
                : QUEUE_STRINGS.FAX_DISCARDED,
            type: 'success',
            id: uuid(),
          })
        )
        navigate('../')
        setIsModalOpen(false)
      } catch (err) {
        console.error(err)
        unlockOnDiscard.current = true
        dispatch(
          addToaster({
            message: QUEUE_STRINGS.ERROR_DISCARDING_FAX,
            type: 'error',
            id: uuid(),
          })
        )
      } finally {
        dispatch(hideLoader())
      }
    } else if (intakeType === QUEUE_STRINGS?.WEB) {
      try {
        dispatch(showLoader())
        const response = await fetchTypeStatusForCommunications(
          NAMESPACES.WEB_ENROLLMENT_STATUS
        )
        const webEnrollmentStatusId = response?.data?.find(
          (item: { value: string }) =>
            item?.value?.toLowerCase() === NAMESPACES.DISCARDED_LOWERCASE
        )?.id
        // Need to check the condition with dev, so Might need later
        // if (webEnrollmentStatusId) {
        await discardWebIntakeApi(
          parseInt(communicationId ?? ''),
          parseInt(entityid ?? ''),
          parseInt(layoutid ?? ''),
          {
            enrollment_status: {
              id: webEnrollmentStatusId,
              _state: RECORD_STATE.CHANGED,
            },
            reason: reason,
          }
        )
        dispatch(
          addToaster({
            message: QUEUE_STRINGS.INTAKE_DISCARDED,
            type: 'success',
            id: uuid(),
          })
        )

        setIsModalOpen(false)
        navigate('../')
        // }
      } catch (err) {
        console.error(err)
        dispatch(
          addToaster({
            message: QUEUE_STRINGS.ERROR_DISCARDING_INTAKE,
            type: 'error',
            id: uuid(),
          })
        )
      } finally {
        dispatch(hideLoader())
      }
    }
  }

  const getWebUrl = async () => {
    if (intakeType !== QUEUE_STRINGS.WEB) return

    try {
      setIsLoading(true)
      const response = await fetchHcpFormPdfUrl(faxDetails?.enrollment_id ?? '')
      if (response?.data?.url) {
        setFaxUrl(response?.data?.url)
        return response?.data?.url
      }
      return faxUrl
    } catch (err) {
      console.log(err)
      return faxUrl
    } finally {
      setIsLoading(false)
    }
  }

  const getFaxUrl = async () => {
    try {
      if (intakeType === QUEUE_STRINGS.WEB) {
        return await getWebUrl()
      }
      setIsLoading(true)
      const attachment = faxDetails?.document?.attachment[currentDocumentIndex]
      if (!attachment) {
        return faxUrl
      }
      const { path, name } = attachment
      const { data } = await fetchDownloadUrlForSingleFIles(
        path,
        name,
        faxDetails?.document?.id
      )
      if (data?.url) {
        setFaxUrl(data.url)
        return data.url
      }
      return faxUrl
    } catch (err) {
      console.log(err)
      return faxUrl
    } finally {
      setIsLoading(false)
    }
  }

  const downloadFile = () => {
    fetch(faxUrl)
      .then((response) => {
        const fileName = `${
          faxDetails?.fax_name ?? faxDetails?.enrollment_id ?? 'intakePdf'
        }.pdf`

        return response.blob().then((blob) => ({ blob, fileName }))
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.download = fileName ?? 'intakePdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        console.error('Error fetching the file:', error)
      })
  }

  const splitFaxFunction = async (splitArray: number[][]) => {
    try {
      dispatch(showLoader())
      if (isOCRReview) {
        await ocrSplitApi({
          batch_uuid: faxDetails?.ocr_outbound_batch?.batch_id ?? '',
          page_ranges: splitArray,
          user_id: String(userId ?? ''),
          enforce: false,
        })
        dispatch(
          addToaster({
            message: QUEUE_STRINGS.SPLIT_FAX_SUCCESS,
            type: 'success',
            id: uuid(),
          })
        )
        setIsSplitFax(false)
        navigate('../')
        return
      }

      const splitPayload = {
        splitArray: splitArray,
        userId: userId ?? 0,
        communicationId: communicationId ?? '',
      }
      const response = await splitFaxApi(splitPayload)
      dispatch(
        addToaster({
          message: QUEUE_STRINGS.SPLIT_DOCUMENT_SUCCESS,
          type: 'success',
          id: uuid(),
        })
      )
      setIsSplitFax(false)
      if (response) {
        const communicationId = response?.data?.[0]?.id
        navigate(`../${communicationId}/${caseEntityId}`)
      }
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          message: QUEUE_STRINGS.SPLIT_DOCUMENT_ERROR,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const checkIfHidden = (isWorkflow: boolean) => (isWorkflow ? 'hidden' : '')
  const getPreviewContentTooltip = () => {
    if (intakeType === QUEUE_STRINGS?.EMAIL) {
      return `${EMAIL_PREVIEW.INTAKE_ID} - ${faxDetails?.fax_name}`
    }
    if (intakeType === QUEUE_STRINGS?.WEB) {
      return `${WEB_PREVIEW.ENROLLMENT_ID} - ${faxDetails?.enrollment_id}`
    }
    return faxIdFormatted
  }
  const previewText = (() => {
    if (intakeType === QUEUE_STRINGS?.EMAIL) {
      return `${EMAIL_PREVIEW.INTAKE_ID} - ${faxDetails?.fax_name}`
    }

    if (intakeType === QUEUE_STRINGS?.WEB) {
      return `${WEB_PREVIEW.ENROLLMENT_ID} - ${faxDetails?.enrollment_id}`
    }

    if (faxDetails?.category_communication_type?.label === QUEUE_STRINGS.MAIL) {
      return `${FAX_PREVIEW.MAIL_ID} - ${faxDetails?.communication_id}`
    }

    return `${FAX_PREVIEW.FAX_ID} - ${faxDetails?.communication_id}`
  })()

  const handleDocumentSelected = async (index: number) => {
    try {
      setIsLoading(true)
      setFaxUrl('')
      setCurrentDocumentIndex(index)
      const attachment = faxDetails?.document?.attachment[index]
      if (!attachment) return
      const { path, name } = attachment
      const { data } = await fetchDownloadUrlForSingleFIles(
        path,
        name,
        faxDetails?.document?.id
      )
      if (data) {
        setFaxUrl(data?.url)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const displayAllTabs = () => {
    if (faxDetails?.document?.attachment?.length === 1) {
      setAllTabs([])
      setActiveTab(null)
      return
    }
    const tabs: TabInterface[] = []
    faxDetails?.document?.attachment.map((attachment, index) =>
      tabs.push({
        label: attachment.alias ?? attachment.name,
        component: null,
        key: index,
      })
    )
    setAllTabs(tabs)
    setActiveTab(tabs[0])
  }
  const handleTabChange = (label: string, index: number) => {
    handleDocumentSelected(index)
    setActiveTab({ label: label, component: null, key: index })
  }

  const isDiscardButtonDisabled = () => {
    return !permissionObject.Queue_Discard_Intakes(
      programPermissions,
      faxDetails?.programs?.id?.toString() ??
        faxDetails?.hcp_program?.id?.toString() ??
        ''
    )
  }

  useEffect(() => {
    if (intakeType === QUEUE_STRINGS.FAX) displayAllTabs()
  }, [intakeType, faxDetails])

  useEffect(() => {
    if (!faxDetails) {
      setIsLoading(true)
      setFaxUrl('')
      return
    }

    if (intakeType === QUEUE_STRINGS.WEB) {
      getWebUrl()
      return
    }

    if (faxDetails?.document?.attachment?.length) {
      handleDocumentSelected(0)
      return
    }

    setIsLoading(false)
  }, [faxDetails, intakeType])

  const isFaxDetailsLoading = !faxDetails || isLoading

  return (
    <div
      className={`flex flex-col bg-white h-full rounded border border-accent-100 resize-panel`}
    >
      {isFaxDetailsLoading ? (
        <div className="flex items-center justify-center w-full h-full min-h-[79vh]">
          <Loader isGlobal={false} loaderHeight="126" loaderWidth="126" />
        </div>
      ) : (
        <>
          <div className="p-2 border-b border-gray-300">
            <div className="p-1 flex justify-between w-full">
              <div className="left-section flex items-center">
                <SvgShiftIcon
                  className="cursor-pointer"
                  onClick={() => setPanelSizeData()}
                />
                <SvgVerticalSplitter />
                <Tooltip content={getPreviewContentTooltip()}>
                  <span className="text-xs text-accent-900 font-bold">
                    {previewText}
                  </span>
                </Tooltip>
              </div>

              <div className="flex items-center gap-2">
                <Tooltip
                  content={
                    isDiscardButtonDisabled()
                      ? QUEUE_STRINGS.DISCARD_BUTTON_TOOLTIP
                      : ''
                  }
                >
                  <button
                    type="button"
                    className={`flex items-center font-primary justify-center font-light text-xs w-[4.313rem] h-[1.75rem] rounded border ${
                      isDiscardButtonDisabled()
                        ? 'border-accent-900 text-accent-900 cursor-not-allowed'
                        : 'text-primary-900 border-primary-500 hover:bg-primary-100 cursor-pointer'
                    } ${checkIfHidden(isWorkflow)}`}
                    onClick={() => discardFax()}
                    data-qa-id="discard-intake"
                    disabled={isDiscardButtonDisabled()}
                  >
                    {QUEUE_STRINGS.DISCARD}
                  </button>
                </Tooltip>
                {intakeType === QUEUE_STRINGS?.FAX && (
                  <button
                    type="button"
                    className={`flex items-center ml-3 font-primary justify-center font-light text-xs w-[4.313rem] h-[1.75rem] leading-3 text-center rounded border ${checkIfHidden(
                      isWorkflow
                    )} ${
                      allTabs.length
                        ? 'border-accent-900 text-accent-900 cursor-not-allowed'
                        : 'text-primary-900 border-primary-500 hover:bg-primary-100 cursor-pointer'
                    }`}
                    onClick={() => setIsSplitFax(true)}
                    data-qa-id="split-fax"
                    disabled={allTabs.length ? true : false}
                  >
                    {faxDetails?.category_communication_type?.label ===
                    QUEUE_STRINGS.MAIL
                      ? QUEUE_STRINGS.SPLIT_MAIL
                      : QUEUE_STRINGS.SPLIT_FAX}
                  </button>
                )}
                <SvgVerticalSplitter
                  className={`ml-3 ${checkIfHidden(isWorkflow)}`}
                />
                <Tooltip content={'Download'}>
                  <CustomButton
                    type="button"
                    color="others"
                    widthClass="w-[40px]"
                    horizontalPaddingClasses="px-0"
                    className="h-[1.75rem]"
                    isFilled={true}
                    onClick={() => downloadFile()}
                    data-qa-id="download-file"
                  >
                    <SvgDownloadGrey />
                  </CustomButton>
                </Tooltip>
                <DocumentPopout
                  source={faxUrl}
                  generateUrl={getFaxUrl}
                  className="h-[1.75rem] flex items-center justify-center mr-3"
                />
                <SvgArrowOut
                  onClick={() => openWindow()}
                  className="cursor-pointer"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (
                      e?.key === ShortcutKeys.Enter ||
                      e?.key === ShortcutKeys.Space
                    ) {
                      openWindow()
                    }
                  }}
                />
              </div>
            </div>
            <FaxInfo
              faxData={faxDetails}
              agentDetails={agentDetails}
              isAePcStatusVisible={isAePcStatusVisible}
              intakeType={intakeType}
            />
          </div>
          <div className="flex-1 min-h-0 relative left-col-wrap flex flex-col">
            <div className="mb-2">
              <Tabs
                tabs={allTabs}
                tabStyling="text-ellipsis max-w-[9rem] min-w-[3rem] overflow-hidden"
                defaultActive={activeTab ?? undefined}
                onChange={(label: string, index: number) =>
                  handleTabChange(label, index)
                }
              />
            </div>
            <div className="flex-1 min-h-0 relative">
              {allTabs.length ? (
                activeTab ? (
                  <CommunicationPreview
                    intakeType={intakeType}
                    previewData={previewData}
                    setPreviewData={setPreviewData}
                    handleOptionClick={handleOptionClick}
                    attachments={attachments}
                    downloadFile={downloadFile}
                    faxUrl={faxUrl}
                    allLayouts={allLayouts}
                  />
                ) : null
              ) : (
                <CommunicationPreview
                  intakeType={intakeType}
                  previewData={previewData}
                  setPreviewData={setPreviewData}
                  handleOptionClick={handleOptionClick}
                  attachments={attachments}
                  downloadFile={downloadFile}
                  faxUrl={faxUrl}
                  allLayouts={allLayouts}
                />
              )}
            </div>
          </div>

          {isModalOpen && (
            <ConfirmDialogWithReason
              open={isModalOpen}
              onCancel={() => setIsModalOpen(false)}
              onConfirm={onSubmit}
              message={NAMESPACES.DISCARD_FAX_CONFIRMATION_MESSAGE}
              width="w-[38.5rem]"
              validationMessage={NAMESPACES.DISCARD_REASON_VALIDATION_MESSAGE}
              showCloseButton={true}
            />
          )}

          {isSplitFax && (
            <Modal
              isOpen={true}
              closeModal={() => setIsSplitFax(false)}
              closeModalOnOutsideClick={false}
              showCloseButton={false}
              className="z-10"
            >
              <SplitFax
                faxDetails={faxDetails}
                defaultFileType={EXTENSIONS.PDF as FileType}
                faxUrl={faxUrl}
                setIsSplitFax={setIsSplitFax}
                splitFaxFunction={splitFaxFunction}
              />
            </Modal>
          )}
        </>
      )}
    </div>
  )
}

export default FaxDetails
